'use client'

/***
 *
 * @file components/FloatingContainer/index.tsx
 * @description FloatingContainer component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Truminds Software System
 * @since 13 Feb 2024
 *
 */

// import "./FloatingContainer.scss";
import classNames from 'classnames'
import { ReactNode, useEffect, useState, } from 'react';

interface FloatingContainerProps {
  /** children components to be rendered within the Container layout */
  children?: ReactNode | never,
  fixedContent?: ReactNode,
  content?: ReactNode,
  position?: string,
  top?: string,
  right?: string,
  bottom?: string,
  left?: string,
  padding?: string,
  margin?: string,
  hide?: boolean,
  type?: string,
  openEventName?: string,
  showOnEvent?: boolean
  closeEventName?: string,
  display?: string,
  id?: string,
  className?: string,
  animated?: boolean,
  animationType?: string
}


const FloatingContainer = (props: FloatingContainerProps) => {
  const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top && window?.document?.getElementsByClassName("styles-module_tooltipWrapper__kqvmR")?.length > 0;
  const { position = "top-left", top = "top-0", bottom = "bottom-0", left = "left-0", right = "right-0", padding = "", margin = "", children, content, fixedContent, display = "show", type = "fixed", id = "" } = props;
  const [show, setShow] = useState(display === "event" ? { showElement: isInContentfulStudio, top, left } : { showElement: true, top, left });

  useEffect(() => {
    if (props?.openEventName && display === "event") {
      const handleEvent = (e: any) => {
        let topPosition = top;
        let leftPosition = left;

        if (position === "clicked area") {
          topPosition = e?.detail?.top;
          leftPosition = e?.detail?.left;
        }

        setShow({
          showElement: true,
          top: topPosition,
          left: leftPosition,
        });
      };

      window.addEventListener(props.openEventName, handleEvent);

      return () => {
        window.removeEventListener(props.openEventName as string, handleEvent);
      };
    }
  }, [props?.openEventName, display, position, left, top]);

  useEffect(() => {
    if (props?.closeEventName && display === "event") {
      const handleClose = (e: any) => {
        setShow((prev) => ({ ...prev, showElement: false }));
      };

      window.addEventListener(props.closeEventName, handleClose);

      return () => {
        window.removeEventListener(props.closeEventName as string, handleClose);
      };
    }
  }, [props?.closeEventName, display]);

  useEffect(() => {
    setShow({ ...show, showElement: display === "event" ? isInContentfulStudio : true })
  }, [display])

  return (
    <div data-testid="FloatingContainer"
      {...(() => { return props?.animated ? { "data-aos": props?.animationType } : {} })()}
      className={classNames(
        isInContentfulStudio ? 'w-full h-full min-h-[300px]' : '',
        "FloatingContainer-container",
        props?.className
      )}>
      {(display !== 'hide') && ((display === "event" && show?.showElement) || display === "show") ?
        <div id={id} className={classNames(
          "z-[90]",
          position === 'top-left' ? `${top} ${left}` : '',
          position === 'bottom-left' ? `${bottom} ${left}` : '',
          position === 'top-right' ? `${top} ${right}` : '',
          position === 'bottom-right' ? `${bottom} ${right}` : '',
          position === 'top-center' ? `left-[50%] ${top} transform translate-x-[-50%]` : '',
          position === 'bottom-center' ? `left-[50%] ${bottom} transform translate-x-[-50%]` : '',
          position === 'center' ? `left-[50%] top-[50%] transform translate-x-[-50%] translate-y-[-50%]` : '',
          position === 'left-center' ? `${left} top-[50%] transform translate-y-[-50%]` : '',
          position === 'right-center' ? `${right} top-[50%] transform translate-y-[-50%]` : '',
          {
            "absolute min-w-[300px] min-h-[100px] border-1 border-dashed border-gray-500": isInContentfulStudio,
            "justify-items-start": ['left-center', 'bottom-left', 'top-left'].includes(position),
            "justify-items-end": ['top-right', 'bottom-right', 'right-center'].includes(position),
            "justify-items-center": ['bottom-center', 'top-center', 'center'].includes(position),
            "content-center": ['left-center', 'right-center', 'center'].includes(position),
            "content-end": ['bottom-center', 'bottom-right', 'bottom-left'].includes(position),
            [`max-w-fit max-h-fit ${type}`]: !isInContentfulStudio,
          },
          `h-auto`,
        )}
          style={{
            padding: padding,
            margin: margin,
            ...(() => {
              if (position === "clicked area")
                return { top: show?.top, left: show?.left }
            })()
          }}>
          {fixedContent}
        </div> : <></>
      }
      {content}
    </div>
  );
};

export default FloatingContainer;
